/*

Marketing Preferences Component
Will be used to display the checkboxes of email subscriptions
under the contact information of the user who placed the order.
This component will be used in the Checkout Page.

*/

import React, {
  useState, useEffect, useContext,
} from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from 'anf-core-react';
import { useCookies } from 'react-cookie';
import { StepsContext } from '../../Steps/StepsContext';

export default function MarketingPreferences({ marketingPreferencesData, stepId }) {
  const marketingPrefBrands = marketingPreferencesData?.brands;
  const [cookies, setCookie] = useCookies(['subscribe']);

  // Use optional context - it might not be available when used outside Steps
  const stepsContext = useContext(StepsContext);

  // Use the same logic as ContinueButton to determine if step is being edited
  const isStepCompleted = stepId && stepsContext?.completedSteps?.includes(stepId);
  const isCurrentStep = stepId && stepId === stepsContext?.currentStep;
  const isEditing = isStepCompleted && isCurrentStep;

  const [selectedBrands, setSelectedBrands] = useState(() => {
    // Use the same logic as ContinueButton: if we're editing a completed step, read from cookies
    if (isEditing && cookies.subscribe) {
      try {
        // Handle both string (JSON) and object cases
        const cookieData = typeof cookies.subscribe === 'string'
          ? JSON.parse(cookies.subscribe)
          : cookies.subscribe;
        const result = marketingPrefBrands?.map((brand) => cookieData[brand.id] === 'Y') ?? [];
        return result;
      } catch (error) {
        // Clear the malformed cookie
        setCookie('subscribe', '', { path: '/' });
        // Fallback to original values if cookie parsing fails
        const fallback = marketingPrefBrands?.map((brand) => brand.selected) ?? [];
        return fallback;
      }
    }

    // Default behavior: use prop values for first-time visits or non-editing states
    const defaults = marketingPrefBrands?.map((brand) => brand.selected) ?? [];
    return defaults;
  });

  const handleCheckboxChange = (index) => {
    const updatedBrands = [...selectedBrands];
    updatedBrands[index] = !updatedBrands[index];
    setSelectedBrands(updatedBrands);
  };

  useEffect(() => {
    const cookieValue = {};
    marketingPrefBrands?.forEach((brand, index) => {
      if (selectedBrands[index]) {
        cookieValue[brand.id] = 'Y';
        // Allow N only if user modify/preference
      } else if (!selectedBrands[index] && selectedBrands[index] !== brand.selected) {
        cookieValue[brand.id] = 'N';
      }
    });

    const cookieValueString = JSON.stringify(cookieValue);
    setCookie('subscribe', cookieValueString, { path: '/' });
  }, [marketingPrefBrands, selectedBrands, setCookie]);

  if (marketingPreferencesData?.hideMarketingPreferences) {
    return null;
  }

  return (
    <div className="marketing-preferences-mfe scope-1892">
      <h3 data-prop={marketingPreferencesData?.headingLabel?.key} className="marketing-preferences-heading">
        {marketingPreferencesData?.headingLabel?.value}
      </h3>
      <div className="marketing-preferences">
        <div className="marketing-preferences-checkboxes">
          {marketingPrefBrands?.map((brand, index) => {
            const isChecked = selectedBrands[index];

            return (
              <Checkbox
                key={brand?.id}
                id={brand?.id}
                name="subscribeToEmails"
                value={isChecked?.toString()}
                description={brand?.label?.value}
                onChange={() => handleCheckboxChange(index)}
                isChecked={isChecked}
              />
            );
          })}
        </div>

      </div>
    </div>
  );
}

MarketingPreferences.propTypes = {
  marketingPreferencesData: PropTypes.shape({
    headingLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    brands: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.shape({
          key: PropTypes.string,
          value: PropTypes.string,
        }),
        selected: PropTypes.bool,
      }),
    ),
    hideMarketingPreferences: PropTypes.bool,
  }),
  stepId: PropTypes.string,
};

MarketingPreferences.defaultProps = {
  marketingPreferencesData: {
    headingLabel: {
      key: '',
      value: 'Get Our Emails',
    },
    brands: [
      {
        id: 'anf',
        label: {
          key: '',
          value: 'Abercrombie & Fitch',
        },
        selected: false,
      },
      {
        id: 'kids',
        label: {
          key: '',
          value: 'abercrombie kids',
        },
        selected: false,
      },
    ],
    hideMarketingPreferences: false,
  },
  stepId: undefined,
};
